import IconComponent from "@mdi/react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { mdiChevronDown, mdiChevronRight } from "@mdi/js";
import styled from "styled-components";
import { ExportFormat, exportFile, exportFileName } from "utils/exportUtils";
import * as XLSX from "xlsx";

import { CopyIconButton, useCopyAcknowledgement } from "components/shared/CopyIconButton";
import { ExportIconButton } from "components/shared/ExportIconButton";

import { ProductionArrayRow } from "../hooks/useProductionArrayData";
import {
  MISSING_VALUE,
  SectionLabel,
  TableCell,
  TableFrame,
  TableHeaderCell,
  TableHeaderRow,
  TableRow
} from "../styles";
import {
  ProductionArrayUnits,
  formatProductionValue as format
} from "../utils/productionArray";
import {
  buildProductionArrayHeaders,
  productionArrayGrid,
  toDelimitedText
} from "../utils/productionArrayExport";

/*
 * The first two columns are fixed, so widening the CARD never widens them: at
 * 76 and 70 the header "Months" was a pixel short of its own column and drew
 * as "Month...". Measured against the real header cell, which spends 20px of
 * its width on padding and 1px on its rule: 79px and 75px of content, so 80
 * and 76 with a pixel to spare.
 */
const COLUMNS = "80px 76px repeat(6, 1fr)";

/*
 * The data table draws these at 18px and 21.9px. The card is a denser thing
 * than the toolbar they came from, so both step down by a sixth and keep the
 * pairing they already had.
 */
const COPY_ICON_SIZE = 1;
const EXPORT_ICON_SIZE_PX = 18;

interface YearGroup {
  readonly year: number;
  readonly label: string;
  readonly monthRange: string;
  readonly rows: readonly ProductionArrayRow[];
  readonly totals: readonly Maybe<number>[];
}

const sum = (
  rows: readonly ProductionArrayRow[],
  read: (r: ProductionArrayRow) => Maybe<number>
) => {
  const values = rows.map(read).filter((v): v is number => v != null);
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0);
};

// Volumes add up across a year; rates do not, so the group row averages them.
const mean = (
  rows: readonly ProductionArrayRow[],
  read: (r: ProductionArrayRow) => Maybe<number>
) => {
  const values = rows.map(read).filter((v): v is number => v != null);
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0) / values.length;
};

const buildGroups = (rows: readonly ProductionArrayRow[]): YearGroup[] => {
  const byYear = new Map<number, ProductionArrayRow[]>();
  const unknown: ProductionArrayRow[] = [];

  for (const row of rows) {
    if (row.calendar == null) {
      unknown.push(row);
      continue;
    }
    const bucket = byYear.get(row.calendar.year) ?? [];
    bucket.push(row);
    byYear.set(row.calendar.year, bucket);
  }

  const groups = Array.from(byYear.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([year, yearRows]) => {
      const ordered = [...yearRows].sort((a, b) => b.monthsIndex - a.monthsIndex);
      const indices = ordered.map((r) => r.monthsIndex);
      return {
        year,
        label: String(year),
        monthRange: `${Math.min(...indices)}–${Math.max(...indices)}`,
        rows: ordered,
        totals: [
          sum(ordered, (r) => r.oilVolume),
          mean(ordered, (r) => r.oilRate),
          sum(ordered, (r) => r.gasVolume),
          mean(ordered, (r) => r.gasRate),
          sum(ordered, (r) => r.waterVolume),
          mean(ordered, (r) => r.waterRate)
        ]
      };
    });

  if (unknown.length > 0) {
    const ordered = [...unknown].sort((a, b) => b.monthsIndex - a.monthsIndex);
    const indices = ordered.map((r) => r.monthsIndex);
    groups.push({
      year: Number.NaN,
      label: MISSING_VALUE,
      monthRange: `${Math.min(...indices)}–${Math.max(...indices)}`,
      rows: ordered,
      totals: [
        sum(ordered, (r) => r.oilVolume),
        mean(ordered, (r) => r.oilRate),
        sum(ordered, (r) => r.gasVolume),
        mean(ordered, (r) => r.gasRate),
        sum(ordered, (r) => r.waterVolume),
        mean(ordered, (r) => r.waterRate)
      ]
    });
  }

  return groups;
};

export function ProductionArrayTable({
  rows,
  units,
  normalizedBy
}: {
  rows: readonly ProductionArrayRow[];
  units: ProductionArrayUnits;
  normalizedBy?: Maybe<string>;
}): JSX.Element {
  const groups = useMemo(() => buildGroups(rows), [rows]);
  const headers = useMemo(() => buildProductionArrayHeaders(units), [units]);
  const newestYear = groups[0]?.label;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { copied, acknowledge } = useCopyAcknowledgement();

  const isExpanded = (label: string) => expanded[label] ?? label === newestYear;

  const toggle = (label: string) =>
    setExpanded((current) => ({ ...current, [label]: !isExpanded(label) }));

  /*
   * Every month the table holds, in the order it draws them, whichever years
   * happen to be open. A collapsed year is a reading aid; an export missing
   * half the array because of one is a file the user has to check.
   *
   * The year rows themselves are left out. Their volumes are sums of the
   * months below them and their rates are averages, so a spreadsheet that
   * totalled the column would count every barrel twice.
   */
  const grid = useMemo(
    () =>
      productionArrayGrid(
        groups.flatMap((group) => group.rows),
        units,
        normalizedBy
      ),
    [groups, units, normalizedBy]
  );

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(toDelimitedText(grid, "\t"));
      acknowledge();
    } catch {
      toast.error("Could not copy the production array");
    }
  }, [grid, acknowledge]);

  /*
   * Both files hold the same grid. The CSV is the text form; the workbook is
   * the same cells handed to `xlsx` as they are, so a number stays a number
   * and a month with no value stays an empty cell rather than a dash.
   */
  const download = useCallback(
    (format: ExportFormat) => {
      if (format === ExportFormat.CSV) {
        exportFile(
          new Blob([toDelimitedText(grid, ",")], { type: "text/csv;charset=utf-8;" }),
          exportFileName(ExportFormat.CSV)
        );
        return;
      }
      const sheet = XLSX.utils.aoa_to_sheet(grid);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Production Array");
      XLSX.writeFile(book, exportFileName(ExportFormat.Excel));
    },
    [grid]
  );

  return (
    <Wrapper>
      <BlockHeader>
        <SectionLabel $gap={0}>Production Array</SectionLabel>
        <Actions>
          <ExportIconButton
            tooltip="Export Production Array"
            iconSizePx={EXPORT_ICON_SIZE_PX}
            onSelect={download}
            data-testid="production-array-download"
          />
          <CopyIconButton
            tooltip="Copy Production Array"
            successMessage="Production Array Copied to Clipboard!"
            copied={copied}
            iconSize={COPY_ICON_SIZE}
            onClick={copy}
            data-testid="production-array-copy"
          />
        </Actions>
      </BlockHeader>
      <TableFrame>
        <TableHeaderRow $columns={COLUMNS}>
          {headers.map((header) => (
            <TableHeaderCell key={header} title={header}>
              {header}
            </TableHeaderCell>
          ))}
        </TableHeaderRow>
        {groups.map((group) => (
          <div key={group.label}>
            <TableRow
              $columns={COLUMNS}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded(group.label)}
              onClick={() => toggle(group.label)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                // Space scrolls the panel body otherwise.
                event.preventDefault();
                toggle(group.label);
              }}
              data-testid={`production-array-year-${group.label}`}>
              <GroupMonthCell>
                <IconComponent
                  path={isExpanded(group.label) ? mdiChevronDown : mdiChevronRight}
                  size={CHEVRON_SIZE}
                />
                {group.label}
              </GroupMonthCell>
              <GroupCell>{group.monthRange}</GroupCell>
              {group.totals.map((total, index) => (
                <GroupCell key={index}>{format(total)}</GroupCell>
              ))}
            </TableRow>
            {isExpanded(group.label) &&
              group.rows.map((row) => (
                <TableRow key={row.date} $columns={COLUMNS}>
                  <MonthCell>
                    <ChevronSlot />
                    {row.calendar?.label ?? MISSING_VALUE}
                  </MonthCell>
                  <TableCell>{row.monthsIndex}</TableCell>
                  <TableCell>{format(row.oilVolume)}</TableCell>
                  <TableCell>{format(row.oilRate)}</TableCell>
                  <TableCell>{format(row.gasVolume)}</TableCell>
                  <TableCell>{format(row.gasRate)}</TableCell>
                  <TableCell>{format(row.waterVolume)}</TableCell>
                  <TableCell>{format(row.waterRate)}</TableCell>
                </TableRow>
              ))}
          </div>
        ))}
      </TableFrame>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  border-top: 1px solid #e3e6e8;
  padding: 10px 14px 14px;
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
`;

/*
 * Right of the heading, in the data table toolbar's order: export, then copy.
 * The buttons are a fixed 36px tall; the negative block margin keeps them from
 * setting the header's height, so the title-to-table gap stays the same 8px
 * the Performance table has. (36px button − 14px label line) / 2 = 11px.
 */
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: -11px 0 -11px auto;
`;

const MonthCell = styled.span`
  padding: 6px 8px 6px 5px;
  font-size: 14px;
  color: var(--color-text);
  display: inline-flex;
  align-items: center;
  gap: 1px;
`;

/**
 * 15px, matching `ChevronSlot` exactly so the group rows and the month rows
 * still line up. `@mdi/react` renders `1.5 x size` rem and this app's root is
 * 62.5%, so one unit is 15px; the old 0.63 drew this at about nine.
 */
const CHEVRON_SIZE = 1;

const ChevronSlot = styled.span`
  width: 15px;
  flex: none;
`;

// Ink, like every other value on the card: teal is the well's name and the
// table headers, and a year row is neither. The tinted background and the
// heavier weight are what still mark it as a group.
const GroupMonthCell = styled(MonthCell)`
  background: #f9fafc;
  color: var(--color-text);
  font-weight: 500;
  cursor: pointer;
`;

const GroupCell = styled(TableCell)`
  background: #f9fafc;
  color: var(--color-text);
  font-weight: 500;
  cursor: pointer;
`;
